import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { ReferralsTable, StatusBadge } from '../../components/admin/DataTables';
import { ReferralProfile } from '../../components/admin/Profiles';
import { KPICard, KPIGrid } from '../../components/admin/KPICards';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { UserPlus, UserCheck, Clock, Award, Search, Filter, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'nuevo_lead', label: 'Leads' },
  { value: 'registrado', label: 'Registrados' },
  { value: 'contactado', label: 'Contactados' },
  { value: 'prueba_agendada', label: 'Visitas agendadas' },
  { value: 'instalado', label: 'Instalados' },
  { value: 'activo', label: 'Activos' },
  { value: 'validado', label: 'Validados' },
  { value: 'recompensa_entregada', label: 'Completados' },
];

export default function AdminReferrals() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [loadingPayment, setLoadingPayment] = useState(null);

  const fetchReferrals = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/referrals`, { 
        withCredentials: true 
      });
      setReferrals(response.data);
    } catch (err) {
      console.error('Error fetching referrals:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleAddPayment = async (referralId) => {
    setLoadingPayment(referralId);
    try {
      await axios.post(
        `${API_URL}/api/admin/referrals/${referralId}/add-payment`,
        {},
        { withCredentials: true }
      );
      await fetchReferrals();
      
      // Update selected referral if it's the one being modified
      if (selectedReferral?.id === referralId) {
        const updated = referrals.find(r => r.id === referralId);
        if (updated) {
          setSelectedReferral({
            ...updated,
            payments_made: Math.min(updated.payments_made + 1, 3)
          });
        }
      }
    } catch (err) {
      console.error('Error adding payment:', err);
    } finally {
      setLoadingPayment(null);
    }
  };

  const handleViewReferral = (referral) => {
    setSelectedReferral(referral);
  };

  // Filter referrals
  const filteredReferrals = referrals.filter(ref => {
    const matchesSearch = 
      ref.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ref.email && ref.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      ref.referrer_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || ref.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const totalReferrals = referrals.length;
  const pendingReferrals = referrals.filter(r => 
    !['validado', 'recompensa_entregada'].includes(r.status)
  ).length;
  const validatedReferrals = referrals.filter(r => r.status === 'validado').length;
  const activeReferrals = referrals.filter(r => r.status === 'activo').length;

  if (loading) {
    return (
      <AdminLayout
        title="Referidos"
        subtitle="Cargando..."
        onLogout={handleLogout}
      >
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Referidos"
      subtitle={`${totalReferrals} en total`}
      pendingRewards={validatedReferrals}
      onLogout={handleLogout}
    >
      {/* Stats */}
      <KPIGrid className="mb-4 lg:mb-8">
        <KPICard
          title="Total"
          value={totalReferrals}
          icon={UserPlus}
        />
        <KPICard
          title="Proceso"
          value={pendingReferrals}
          icon={Clock}
        />
        <KPICard
          title="Activos"
          value={activeReferrals}
          icon={UserCheck}
        />
        <KPICard
          title="Validados"
          value={validatedReferrals}
          icon={Award}
          variant={validatedReferrals > 0 ? 'success' : 'default'}
        />
      </KPIGrid>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 lg:gap-4 mb-4 lg:mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 lg:w-5 lg:h-5 text-zinc-500" />
          <Input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar..."
            className="pl-10 lg:pl-11 h-10 lg:h-12 bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 focus:border-white/30 rounded-lg text-sm"
            data-testid="search-referrals-input"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px] lg:w-[220px] h-10 lg:h-12 bg-[#0A0A0A] border-white/10 text-white text-sm">
            <Filter className="w-4 h-4 mr-2 text-zinc-500" />
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            {STATUS_OPTIONS.map(option => (
              <SelectItem 
                key={option.value} 
                value={option.value}
                className="text-zinc-300 focus:text-white focus:bg-white/10 text-sm"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(searchTerm || statusFilter !== 'all') && (
          <Button
            variant="ghost"
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
            }}
            className="h-10 lg:h-12 text-zinc-400 hover:text-white hover:bg-white/10 text-sm"
          >
            <X className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Limpiar</span>
          </Button>
        )}
      </div>

      {/* Active Filters Display */}
      {statusFilter !== 'all' && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-zinc-500">Filtrado por:</span>
          <StatusBadge status={statusFilter} />
          <span className="text-xs text-zinc-400">
            ({filteredReferrals.length} {filteredReferrals.length === 1 ? 'resultado' : 'resultados'})
          </span>
        </div>
      )}

      {/* Referrals Table */}
      <ReferralsTable
        referrals={filteredReferrals}
        searchTerm={searchTerm}
        onViewReferral={handleViewReferral}
        onAddPayment={handleAddPayment}
        loadingPayment={loadingPayment}
      />

      {/* Referral Profile Sidebar */}
      {selectedReferral && (
        <ReferralProfile
          referral={selectedReferral}
          onClose={() => setSelectedReferral(null)}
          onAddPayment={handleAddPayment}
          loadingPayment={loadingPayment}
        />
      )}
    </AdminLayout>
  );
}
